import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { marketingBackend } from '../../../services/marketingBackend'
import { loadLibrary, removeFromLibrary, subscribeLibrary } from '../../../services/imageLibrary'
import { hairdresserService } from '../../../../../_services/hairdresser.service'

// In-panel image picker, in the builder's side panel. Everything here is
// the salon's own: what they've uploaded before (kept for one-tap reuse),
// their portfolio from their profile, and the photos from Settings > Salon.
// (An AI tab returns once there's a generation endpoint; see
// docs/EMAIL_HTML_BACKEND.md.)

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024
const MAX_EDGE = 1600

// Decode the file (fails for formats the browser can't show, like HEIC in
// Chrome) and scale big photos down. GIFs pass through untouched so they
// keep their animation; PNGs stay PNG so logos keep their transparency.
const prepareImage = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file)
  const img = new Image()
  img.onload = () => {
    URL.revokeObjectURL(url)
    const w = img.naturalWidth
    const h = img.naturalHeight
    const scale = Math.min(1, MAX_EDGE / Math.max(w, h))
    if (file.type === 'image/gif' || (scale === 1 && file.size < 1.5 * 1024 * 1024)) { resolve(file); return }
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(w * scale)
    canvas.height = Math.round(h * scale)
    const png = file.type === 'image/png'
    const ctx = canvas.getContext('2d')
    if (!png) { ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, canvas.width, canvas.height) }
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
    canvas.toBlob((blob) => {
      if (!blob) { resolve(file); return }
      const base = (file.name || 'image').replace(/\.[^.]+$/, '')
      resolve(new File([blob], `${base}.${png ? 'png' : 'jpg'}`, { type: png ? 'image/png' : 'image/jpeg' }))
    }, png ? 'image/png' : 'image/jpeg', 0.86)
  }
  img.onerror = () => {
    URL.revokeObjectURL(url)
    const err = new Error('unreadable')
    err.unreadable = true
    reject(err)
  }
  img.src = url
})

const TABS = [
  { id: 'uploads', label: 'Uploads' },
  { id: 'portfolio', label: 'Portfolio' },
  { id: 'salon', label: 'Salon' },
]

// A tile fills with the picture, unless it's a long, thin one (a wordmark
// logo), which is shown whole so it can be recognised
const TileImage = ({ src }) => {
  const [fit, setFit] = useState('cover')
  return (
    <img
      src={src}
      alt=""
      loading="lazy"
      onLoad={(e) => { const r = e.currentTarget.naturalWidth / (e.currentTarget.naturalHeight || 1); if (r > 1.6 || r < 0.6) setFit('contain') }}
      style={{ width: '100%', height: '100%', objectFit: fit, padding: fit === 'contain' ? '10%' : 0, boxSizing: 'border-box', display: 'block' }}
    />
  )
}

const ImageGrid = ({ images, onPick, onRemove, cols = 3 }) => (
  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '8px' }}>
    {images.map(img => (
      <div key={img.url} style={{ position: 'relative' }}>
        <button
          type="button"
          onClick={() => onPick(img.url)}
          aria-label={img.name ? `Use ${img.name}` : 'Use this image'}
          className="eb-img-tile"
          style={{
            display: 'block',
            width: '100%',
            padding: 0,
            border: '1px solid #ECECF0',
            borderRadius: '10px',
            overflow: 'hidden',
            cursor: 'pointer',
            aspectRatio: '1',
            backgroundColor: '#F7F7F9',
          }}
        >
          <TileImage src={img.thumb || img.url} />
        </button>
        {onRemove && (
          <button
            type="button"
            onClick={() => onRemove(img.url)}
            aria-label="Remove from your uploads"
            title="Remove from your uploads"
            style={{ position: 'absolute', top: 4, right: 4, width: 22, height: 22, borderRadius: '50%', border: 'none', background: 'rgba(26,26,26,0.72)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        )}
      </div>
    ))}
  </div>
)

const Empty = ({ title, children }) => (
  <div style={{ padding: '26px 14px', textAlign: 'center', border: '1px solid #ECECF0', borderRadius: '12px' }}>
    <p style={{ margin: '0 0 4px', fontSize: '13.5px', fontWeight: 600, color: '#1a1a1a' }}>{title}</p>
    <p style={{ margin: 0, fontSize: '12.5px', lineHeight: 1.5, color: '#6b7280' }}>{children}</p>
  </div>
)

const Hint = ({ children }) => (
  <p style={{ margin: '10px 2px 0', fontSize: '12px', lineHeight: 1.5, color: '#9ca3af' }}>{children}</p>
)

// The salon's photos from Settings: the email header image, the main
// photo and the gallery, without the default placeholder.
const salonPhotos = (salon) => {
  if (!salon) return []
  const urls = [
    salon.emailHeaderImage && salon.emailHeaderImageUrl,
    salon.imageUrl,
    ...((Array.isArray(salon.galleryImages) ? salon.galleryImages : [])
      .slice()
      .sort((x, y) => (x.order || 0) - (y.order || 0))
      .map(g => g && (g.url || g.imageUrl))),
  ].filter(u => typeof u === 'string' && /^https?:/.test(u) && !/salonpic/.test(u))
  return [...new Set(urls)].map(url => ({ url }))
}

export const ImagePanel = ({ onSelect, salon }) => {
  const [library, setLibrary] = useState(() => loadLibrary())
  const [activeTab, setActiveTab] = useState('uploads')
  const [uploadError, setUploadError] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [preview, setPreview] = useState(null)
  const [lastFile, setLastFile] = useState(null)
  const [dragging, setDragging] = useState(false)

  useEffect(() => subscribeLibrary(() => setLibrary(loadLibrary())), [])

  // Portfolio: the signed-in stylist's work from their profile. The store
  // usually has it; otherwise ask the profile endpoint once.
  const storePortfolio = useSelector(state => (
    (state.user && state.user.userInfo && state.user.userInfo.hairdresser && state.user.userInfo.hairdresser.portfolio)
    || (state.hairdresser && state.hairdresser.userInfo && state.hairdresser.userInfo.hairdresser && state.hairdresser.userInfo.hairdresser.portfolio)
    || null
  ))
  const [fetchedPortfolio, setFetchedPortfolio] = useState(null)
  useEffect(() => {
    if (activeTab !== 'portfolio' || fetchedPortfolio || (storePortfolio && storePortfolio.length)) return undefined
    let alive = true
    hairdresserService.getProfileV2()
      .then((res) => {
        const rd = res && res.data && res.data.responseData
        if (alive) setFetchedPortfolio(Array.isArray(rd && rd.portfolio) ? rd.portfolio : [])
      })
      .catch(() => { if (alive) setFetchedPortfolio([]) })
    return () => { alive = false }
  }, [activeTab]) // eslint-disable-line react-hooks/exhaustive-deps
  const portfolioSource = (storePortfolio && storePortfolio.length) ? storePortfolio : (fetchedPortfolio || [])
  const portfolio = portfolioSource
    .map(p => ({ url: p && (p.imageUrl || p.url) }))
    .filter(p => typeof p.url === 'string' && /^https?:/.test(p.url))
  const portfolioLoading = activeTab === 'portfolio' && !(storePortfolio && storePortfolio.length) && fetchedPortfolio === null
  const salonImages = salonPhotos(salon)

  const handleFile = (file) => {
    if (!file) return
    const isImage = /^image\//.test(file.type) || /\.(jpe?g|png|gif|webp|heic|heif)$/i.test(file.name || '')
    if (!isImage) { setUploadError('That file is not an image. Try a JPG or PNG.'); return }
    if (file.size > MAX_UPLOAD_BYTES) { setUploadError('Images need to be under 10MB.'); return }
    setUploadError(null)
    setUploading(true)
    setLastFile(file)
    // Photos are shrunk in the browser first (email shows them at 600px, so
    // 1600px covers retina with room to spare), then uploaded to S3 and the
    // URL goes in the block. No data-URL fallback: Gmail and Outlook block
    // inline images, so the email would go out with a broken picture.
    prepareImage(file)
      .then((ready) => {
        setPreview(URL.createObjectURL(ready))
        return marketingBackend.uploadImage(ready)
      })
      .then((res) => {
        if (!res || !res.url) throw new Error('No image address came back')
        setUploading(false)
        setPreview((p) => { if (p) URL.revokeObjectURL(p); return null })
        onSelect(res.url)
      })
      .catch((err) => {
        setUploading(false)
        setPreview((p) => { if (p) URL.revokeObjectURL(p); return null })
        setUploadError(err && err.unreadable
          ? 'We can’t read that image. iPhone photos (HEIC) need saving as a JPG first.'
          : 'That didn’t upload. Check your connection.')
      })
  }

  return (
    <div>
      {/* Tab strip */}
      <div style={{
        display: 'flex',
        backgroundColor: '#F2F2F7',
        borderRadius: '10px',
        padding: '3px',
        marginBottom: '14px'
      }}>
        {TABS.map(tab => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            style={{
              flex: 1,
              padding: '8px 4px',
              border: 'none',
              borderRadius: '8px',
              backgroundColor: activeTab === tab.id ? '#fff' : 'transparent',
              boxShadow: activeTab === tab.id ? '0 1px 4px rgba(0,0,0,0.10)' : 'none',
              color: activeTab === tab.id ? '#1a1a1a' : '#6b7280',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer',
              fontFamily: 'inherit',
              transition: 'background 0.12s, color 0.12s'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Upload */}
      {activeTab === 'uploads' && (
        <div>
          {/* A real <label> around the input, so the file chooser opens
              natively everywhere (webviews and some browsers ignore a
              scripted click on a hidden input) */}
          <label
            onDragOver={(e) => { e.preventDefault(); if (!dragging) setDragging(true) }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); if (!uploading) handleFile(e.dataTransfer.files && e.dataTransfer.files[0]) }}
            style={{
              display: 'block',
              position: 'relative',
              overflow: 'hidden',
              border: `2px dashed ${dragging ? '#007EA0' : '#d1d5db'}`,
              backgroundColor: dragging ? 'rgba(0,126,160,0.05)' : 'transparent',
              borderRadius: '12px',
              padding: preview ? 0 : '34px 16px',
              minHeight: preview ? '180px' : 0,
              textAlign: 'center',
              cursor: uploading ? 'progress' : 'pointer',
              fontFamily: 'inherit',
              transition: 'border-color 0.2s cubic-bezier(0.32, 0.72, 0, 1), background-color 0.2s cubic-bezier(0.32, 0.72, 0, 1)'
            }}
          >
            <input
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp,image/*"
              disabled={uploading}
              onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; handleFile(f) }}
              style={{ position: 'absolute', width: 1, height: 1, opacity: 0, overflow: 'hidden', clip: 'rect(0 0 0 0)', pointerEvents: 'none' }}
            />
            {preview ? (
              <>
                <img src={preview} alt="" style={{ display: 'block', width: '100%', height: '180px', objectFit: 'cover', opacity: 0.55 }} />
                <span style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', fontSize: '13px', fontWeight: '600', color: '#1a1a1a' }}>
                  <span style={{ width: '28px', height: '28px', border: '3px solid rgba(0,0,0,0.12)', borderTopColor: '#29282D', borderRadius: '50%', animation: 'eb-img-spin 0.9s linear infinite' }} />
                  Uploading…
                </span>
              </>
            ) : (
              <>
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={dragging ? '#007EA0' : '#9ca3af'} strokeWidth="1.5" style={{ margin: '0 auto 12px', display: 'block' }}>
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                <span style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
                  {uploading ? 'Getting your image ready…' : dragging ? 'Drop it here' : 'Drop an image here or tap to upload'}
                </span>
                <span style={{ display: 'block', fontSize: '12px', color: '#9ca3af' }}>
                  PNG, JPG or GIF, up to 10MB
                </span>
              </>
            )}
          </label>
          <style>{'@keyframes eb-img-spin { to { transform: rotate(360deg); } }'}</style>
          {uploadError && (
            <p style={{ fontSize: '12.5px', color: '#B14438', margin: '10px 2px 0', lineHeight: 1.45 }}>
              {uploadError}
              {lastFile && !/read that image/.test(uploadError) && (
                <>
                  {' '}
                  <button type="button" onClick={() => handleFile(lastFile)} style={{ padding: 0, border: 'none', background: 'none', color: '#1a1a1a', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit' }}>
                    Try again
                  </button>
                </>
              )}
            </p>
          )}
          {library.length > 0 && (
            <div style={{ marginTop: 18 }}>
              <p style={{ margin: '0 2px 8px', fontSize: '12px', fontWeight: 600, color: '#6b7280' }}>Your uploads</p>
              <ImageGrid images={library} onPick={onSelect} onRemove={(url) => removeFromLibrary(url)} />
              <Hint>Everything you upload is kept here, ready to use again.</Hint>
            </div>
          )}
        </div>
      )}

      {/* Portfolio / Salon */}
      {activeTab === 'portfolio' && (
        portfolioLoading ? (
          <Hint>Loading your portfolio…</Hint>
        ) : portfolio.length ? (
          <>
            <ImageGrid images={portfolio} onPick={onSelect} />
            <Hint>Your work from your profile. Add more in Settings &gt; Profile.</Hint>
          </>
        ) : (
          <Empty title="Your portfolio is empty">Add photos of your work in Settings &gt; Profile and they’ll be ready here.</Empty>
        )
      )}
      {activeTab === 'salon' && (
        salon === undefined ? (
          <Hint>Loading your salon photos…</Hint>
        ) : salonImages.length ? (
          <>
            <ImageGrid images={salonImages} onPick={onSelect} />
            <Hint>Your salon photos and gallery from Settings &gt; Salon.</Hint>
          </>
        ) : (
          <Empty title="No salon photos yet">Add your salon photo and gallery in Settings &gt; Salon and they’ll be ready here.</Empty>
        )
      )}
    </div>
  )
}
